import type { Filter } from '@cubejs-client/core'

import type { Granularity } from '@/lib/cube-report'
import { shiftDateInput } from '@/lib/format'

/**
 * Çapraz filtre (docs/BI-PLAN.md §5–6): bir parçadaki tıklama, aynı kısa adlı üyesi
 * olan diğer parçaları süzer. Saf modül: React ve Cube istemcisi yok.
 */

export interface Selection {
  /** Seçimi yayan parçanın anahtarı (pano: widget.key; keşfet: `lb.<kısaAd>`, `pv`). */
  source: string
  /** Üyenin kısa adı (`firma`). Aynı kısa ad = aynı alan. */
  member: string
  /** Seçili değerler; zaman kovası için tek öğe `from..to` (YYYY-MM-DD..YYYY-MM-DD). */
  values: string[]
  kind: 'value' | 'range'
}

const RANGE_SEPARATOR = '..'
const isRange = (value: string) => value.includes(RANGE_SEPARATOR)

/** URL parametresi `x` (tekrarlı): `<source>~<member>~<value>`; değer `~` içerebilir. */
export function parseSelections(raw: string[]): Selection[] {
  const selections: Selection[] = []
  for (const item of raw) {
    const first = item.indexOf('~')
    const second = first < 0 ? -1 : item.indexOf('~', first + 1)
    if (second < 0) continue
    const source = item.slice(0, first)
    const member = item.slice(first + 1, second)
    const value = item.slice(second + 1)
    if (!source || !member || !value) continue
    const existing = selections.find((s) => s.source === source && s.member === member)
    if (existing) existing.values.push(value)
    else
      selections.push({ source, member, values: [value], kind: isRange(value) ? 'range' : 'value' })
  }
  return selections
}

export function serializeSelections(selections: Selection[]): string[] {
  return selections.flatMap((s) => s.values.map((value) => `${s.source}~${s.member}~${value}`))
}

export interface SelectionInput {
  source: string
  member: string
  value: string
  kind: Selection['kind']
}

/**
 * Tekil tık: aynı değer seçiliyse kaldırır, değilse o kaynağın o üyedeki seçimini
 * bu değer yapar. `additive` (Ctrl/Meta): değeri ekler ya da çıkarır. Zaman aralığı
 * her zaman tek değerlidir (`additive` yok sayılır). Diğer kaynaklara dokunulmaz.
 */
export function toggleSelection(
  current: Selection[],
  next: SelectionInput,
  additive: boolean,
): Selection[] {
  const same = (s: Selection) => s.source === next.source && s.member === next.member
  const existing = current.find(same)
  const rest = current.filter((s) => !same(s))
  const held = existing?.values ?? []
  let values: string[]
  if (additive && next.kind === 'value') {
    values = held.includes(next.value)
      ? held.filter((v) => v !== next.value)
      : [...held, next.value]
  } else {
    values = held.length === 1 && held[0] === next.value ? [] : [next.value]
  }
  if (values.length === 0) return rest
  const merged: Selection = { source: next.source, member: next.member, values, kind: next.kind }
  // Var olan seçim yerinde kalsın: chip sırası tıklamayla oynamasın.
  return existing ? current.map((s) => (same(s) ? merged : s)) : [...rest, merged]
}

export interface SelectionTarget {
  /** Süzülen parçanın anahtarı: kendi seçimine süzülmez. */
  key: string
  view: string
  fields: ReadonlyMap<string, unknown>
  receive: boolean
  /** `ignoreFilters` (kısa adlar). */
  ignored: ReadonlySet<string>
}

export function selectionFilters(selections: Selection[], target: SelectionTarget): Filter[] {
  if (!target.receive) return []
  return selections.flatMap((s): Filter[] => {
    const member = `${target.view}.${s.member}`
    if (s.source === target.key || target.ignored.has(s.member) || !target.fields.has(member)) {
      return []
    }
    if (s.kind === 'range') {
      return [{ member, operator: 'inDateRange', values: s.values[0].split(RANGE_SEPARATOR) }]
    }
    return [{ member, operator: 'equals', values: s.values }]
  })
}

export function isSelected(
  selections: Selection[],
  source: string,
  member: string,
  value: string,
): boolean {
  return selections.some(
    (s) => s.source === source && s.member === member && s.values.includes(value),
  )
}

function lastDayOfMonth(year: number, month: number): string {
  const pad = (n: number) => String(n).padStart(2, '0')
  const [y, m] = month === 12 ? [year + 1, 1] : [year, month + 1]
  return shiftDateInput(`${y}-${pad(m)}-01`, -1)
}

/**
 * Cube zaman kovası (`2026-09-01T00:00:00.000`, İstanbul duvar saati) -> `from..to`
 * takvim günleri. Metinden okunur, `new Date` kullanılmaz (bkz. `formatCubeDateTime`).
 */
export function bucketRange(bucket: string, granularity: Granularity): string {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(bucket)
  if (!match) return bucket
  const from = `${match[1]}-${match[2]}-${match[3]}`
  const year = Number(match[1])
  const month = Number(match[2])
  let to: string
  switch (granularity) {
    case 'day':
      to = from
      break
    case 'week':
      to = shiftDateInput(from, 6)
      break
    case 'month':
      to = lastDayOfMonth(year, month)
      break
    case 'quarter':
      to = lastDayOfMonth(year, month + 2)
      break
    case 'year':
      to = `${year}-12-31`
      break
  }
  return `${from}${RANGE_SEPARATOR}${to}`
}
